import router from 'routes'
import { CROSS_DOMAIN_TRACKING_KEY, DISALLOWED_QUERIES } from 'constants/tripla'
import { allowBypassSiteMaintenance } from './maintenance'
import { BwPath } from 'constants/ota'
import { OtaPath } from 'constants/bookingWidget'
import { genQueryParamsResultPage } from 'utilities/generateSearchForm'
import store from 'store'

export const replaceQuery = ({ exclude = [], overrides = {} }) => {
  const query = { ...router.currentRoute.query, ...overrides }
  const allExcluedQuery = [...exclude, ...DISALLOWED_QUERIES]

  const filteredQuery = Object.fromEntries(
    Object.entries(query).filter(([key]) => !allExcluedQuery.includes(key))
  )

  return router.replace({ query: filteredQuery }).catch(ignoreNavigationDuplicated)
}

export const clearQuery = () => {
  router.replace({ query: {} }).catch(ignoreNavigationDuplicated)
}

export const redirectToExternalLink = (url, options = {}) => {
  const { innerText = '', isOpenInNewTab = false, target } = options
  const urlObj = new URL(url)

  if (!urlObj.searchParams[CROSS_DOMAIN_TRACKING_KEY]) {
    urlObj.searchParams.set(CROSS_DOMAIN_TRACKING_KEY, getLinkerParameter(url))
  }

  const a = document.createElement('a')
  a.href = urlObj.toString()
  a.style.display = 'none'
  a.innerText = innerText
  a.target = isOpenInNewTab ? '_blank' : target || '_self'

  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export function getLinkerParameter(url) {
  const formNode = document.createElement('FORM')

  formNode.action = url
  formNode.style.opacity = '0'

  formNode.addEventListener('submit', (event) => {
    event.preventDefault()
  })

  const buttonNode = document.createElement('BUTTON')

  buttonNode.type = 'submit'
  formNode.append(buttonNode)
  document.body.append(formNode)

  buttonNode.click()

  const _glNode = formNode.querySelector('input[name="_gl"]')
  const value = _glNode ? _glNode.value : ''

  formNode.remove()

  return value
}

export function ignoreNavigationDuplicated(err) {
  if (err.name !== 'NavigationDuplicated') {
    throw err
  }
}

/**
 * Add maintenance_mode=false to query params for bypassing widget's maintenance.
 *
 * @param {string} url
 * @returns string
 */
export function addMaintenanceModeParam(url) {
  if (allowBypassSiteMaintenance()) {
    const urlObj = new URL(url, window.location.origin)
    urlObj.searchParams.set('maintenance_mode', 'false')
    return urlObj.toString()
  }

  return url
}

export function getBwPageUrl({ path, query }) {
  const hotelBrand = store.getters['setting/getHotelBrand']
  const isIframeBookingWidgetWithSubdomainEnabled = hotelBrand?.integrations.some(
    (i) => i.integration_type === 'iframe_booking_widget_with_subdomain_enabled' && i.active
  )
  const initialSetting = store.getters['setting/getInit']
  const isSubdomainMode =
    !isIframeBookingWidgetWithSubdomainEnabled && initialSetting.standalone_settings?.url
  const hotelURL = isSubdomainMode
    ? initialSetting.standalone_settings?.url
    : initialSetting.confirmation_active
    ? initialSetting.confirmation_link
    : initialSetting.hotel_url || store.getters['setting/getBWOrigin']
  const bwURL = new URL(hotelURL)

  if (isSubdomainMode) {
    bwURL.pathname = path

    for (const [key, value] of Object.entries(query)) {
      bwURL.searchParams.append(key, value)
    }

    return bwURL.toString()
  }

  const { code, ...params } = query
  const urlSearchParams = new URLSearchParams(params)

  bwURL.searchParams.set('code', code)
  bwURL.searchParams.set('tripla_booking_widget_open', `${path}?${urlSearchParams.toString()}`)

  return bwURL.toString()
}

export function getBwSearchResultPageUrl() {
  const isBrandFacility = store.getters['setting/isBrandFacility']
  const path = isBrandFacility ? BwPath.FACILITIES : BwPath.SEARCH_PAGE
  const searchForm = store.getters['search/getSearchForm']
  const isTiersMode = store.getters['setting/isTiersMode']
  const isMcpMode = store.getters['setting/isMcpMode']
  const query = genQueryParamsResultPage(searchForm, isTiersMode, isMcpMode)

  return getBwPageUrl({ path, query })
}

export function getBwConfirmationPageUrl({ current_reservation, code, bypass_token }) {
  if (!current_reservation) return

  const path = `${BwPath.CONFIRMATION}/${current_reservation}`
  const query = { code, bypass_token }

  return getBwPageUrl({ path, query })
}

export function getOtaPageUrl({ path, query }) {
  const otaURL = new URL(`${process.env.OTA_DOMAIN}`)

  otaURL.pathname = path

  for (const [key, value] of Object.entries(query)) {
    otaURL.searchParams.append(key, value)
  }

  return otaURL.toString()
}

export function getOtaConfirmationPageUrl(payload) {
  const { current_reservation, ...query } = payload

  if (!current_reservation) return

  const path = `${OtaPath.CONFIRMATION}/${current_reservation}`

  return getOtaPageUrl({ path, query })
}
